import React, { useEffect, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { toast } from "react-toastify";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api/axios";
import PaymentGatewayDetails from "../components/PaymentGatewayDetails";
import { dateTimeLocalToIso, toDateTimeLocalValue } from "../utils/dateTime";
import {
  getOptionTemplateQuantity,
  normaliseSelectedOptions,
} from "../utils/orderItemQuantity";
import { getOrderItemTaxDetails } from "../utils/orderItemTax";
import "../assets/styles/page.css";
import "../assets/styles/orders-table.css";

import { GrPrevious, GrNext } from "react-icons/gr";
import { Tooltip } from "react-tooltip";
import {
  TbEyeSearch,
  TbPackage,
  TbRefresh,
  TbCurrencyRupee,
  TbAlertTriangle,
  TbReceiptRefund,
  TbBellRinging,
  TbSearch,
  TbAdjustmentsHorizontal,
  TbX,
  TbBuildingFactory2,
} from "react-icons/tb";

const PAGE_SIZE = 10;

const PAGE_NAME = "Orders";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const orderFilters = [
  { label: "All orders", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Processing", value: "processing" },
  { label: "Preliminary check", value: "preliminary_check" },
  { label: "Printing", value: "printing" },
  { label: "Quality check", value: "quality_check" },
  { label: "Shipped", value: "shipped" },
  { label: "Delivered", value: "delivered" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Failed", value: "failed" },
];

const paymentFilters = [
  { label: "All payment statuses", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Paid", value: "paid" },
  { label: "Failed", value: "failed" },
  { label: "Refund pending", value: "refund_pending" },
  { label: "Refunded", value: "refunded" },
];

const shippingFilters = [
  { label: "All fulfilment statuses", value: "" },
  { label: "Not required", value: "not_required" },
  { label: "Pending", value: "pending" },
  { label: "Shipped", value: "shipped" },
  { label: "Delivered", value: "delivered" },
  { label: "Returned", value: "returned" },
];

const questionnaireFilters = [
  { label: "All questionnaire statuses", value: "" },
  { label: "Pending", value: "pending" },
  { label: "Draft", value: "draft" },
  { label: "Submitted", value: "submitted" },
  { label: "Reopened", value: "reopened" },
];

const emptyOrderFilters = {
  order_status: "",
  payment_status: "",
  shipping_status: "",
  questionnaire_status: "",
  payment_method: "",
  date_from: "",
  date_to: "",
  min_amount: "",
  max_amount: "",
};

const orderStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "Confirmed", value: "confirmed" },
  { label: "Processing", value: "processing" },
  { label: "Preliminary check", value: "preliminary_check" },
  { label: "Printing", value: "printing" },
  { label: "Quality check", value: "quality_check" },
  { label: "Shipped", value: "shipped" },
  { label: "Delivered", value: "delivered" },
  { label: "Cancelled", value: "cancelled" },
  { label: "Failed", value: "failed" },
];

const getPaginationItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const visible = new Set([
    1,
    totalPages,
    currentPage,
    currentPage - 1,
    currentPage + 1,
  ]);
  const pages = [...visible]
    .filter((page) => page >= 1 && page <= totalPages)
    .sort((left, right) => left - right);
  const items = [];
  pages.forEach((page, index) => {
    if (index > 0 && page - pages[index - 1] > 1) {
      items.push(`ellipsis-${page}`);
    }
    items.push(page);
  });
  return items;
};

const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;
const toPaise = (value) => Math.round(Number(value || 0) * 100);

const emptyShippingForm = {
  courier_name: "",
  tracking_number: "",
  tracking_url: "",
  estimated_delivery_date: "",
};

const emptyRefundForm = {
  amount: "",
  remark: "",
};

const getDefaultProductionDeadline = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
};

const emptyProductionForm = {
  assigned_to: "",
  assignee_contact: "",
  target_completion_at: "",
  priority: "normal",
  production_brief: "",
};

const Orders = () => {
  const [searchParams] = useSearchParams();
  const linkedOrderId = searchParams.get("order_id");
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ ...emptyOrderFilters });
  const [appliedFilters, setAppliedFilters] = useState({ ...emptyOrderFilters });
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [viewSidebarOpen, setViewSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const [refundDialog, setRefundDialog] = useState(false);
  const [refundSubmitting, setRefundSubmitting] = useState(false);
  const [refundForm, setRefundForm] = useState(emptyRefundForm);
  const [refundOtpDialog, setRefundOtpDialog] = useState(false);
  const [refundOtp, setRefundOtp] = useState("");
  const [refundOtpInfo, setRefundOtpInfo] = useState(null);
  const [refundOtpSending, setRefundOtpSending] = useState(false);
  const [refundOtpResendAt, setRefundOtpResendAt] = useState(0);
  const [refundOtpNow, setRefundOtpNow] = useState(Date.now());
  const [shippingDialog, setShippingDialog] = useState(false);
  const [shippingForm, setShippingForm] = useState(emptyShippingForm);
  const [shippingSubmitting, setShippingSubmitting] = useState(false);
  const [productionSidebarOpen, setProductionSidebarOpen] = useState(false);
  const [productionSubmitting, setProductionSubmitting] = useState(false);
  const [productionForm, setProductionForm] = useState(emptyProductionForm);
  const [printingDialog, setPrintingDialog] = useState(false);
  const [printingVendors, setPrintingVendors] = useState([]);
  const [printingVendorId, setPrintingVendorId] = useState("");
  const [printingVendorsLoading, setPrintingVendorsLoading] = useState(false);
  const [printingSubmitting, setPrintingSubmitting] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });

  const fetchOrders = async (
    page = 1,
    selectedFilters = appliedFilters,
    selectedSearch = debouncedSearch
  ) => {
    try {
      setLoading(true);

      const res = await api.get("/admin/orders", {
        params: {
          page,
          limit: PAGE_SIZE,
          search: selectedSearch.trim() || undefined,
          ...Object.fromEntries(
            Object.entries(selectedFilters).map(([key, value]) => [
              key,
              String(value || "").trim() || undefined,
            ])
          ),
        },
        headers: getPermissionHeaders("view"),
      });

      setOrders(res.data?.data || []);
      const nextPagination = res.data?.pagination || {
        page,
        total: 0,
        totalPages: 1,
      };
      setPagination({
        ...nextPagination,
        totalPages: Math.max(Number(nextPagination.totalPages) || 0, 1),
      });
    } catch (error) {
      showRequestError(error, "Failed to fetch orders.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    fetchOrders(1, appliedFilters, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, debouncedSearch]);

  const updateFilterDraft = (key, value) => {
    setFilterDraft((current) => ({ ...current, [key]: value }));
  };

  const applyFilters = (event) => {
    event?.preventDefault();
    if (
      filterDraft.date_from &&
      filterDraft.date_to &&
      filterDraft.date_from > filterDraft.date_to
    ) {
      toast.error("From date cannot be after to date.");
      return;
    }
    if (
      filterDraft.min_amount !== "" &&
      filterDraft.max_amount !== "" &&
      Number(filterDraft.min_amount) > Number(filterDraft.max_amount)
    ) {
      toast.error("Minimum amount cannot be greater than maximum amount.");
      return;
    }
    setAppliedFilters({ ...filterDraft });
  };

  const clearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setFilterDraft({ ...emptyOrderFilters });
    setAppliedFilters({ ...emptyOrderFilters });
  };

  const activeFilterLabels = [
    appliedFilters.order_status &&
      `Order: ${orderFilters.find((item) => item.value === appliedFilters.order_status)?.label}`,
    appliedFilters.payment_status &&
      `Payment: ${paymentFilters.find((item) => item.value === appliedFilters.payment_status)?.label}`,
    appliedFilters.shipping_status &&
      `Fulfilment: ${shippingFilters.find((item) => item.value === appliedFilters.shipping_status)?.label}`,
    appliedFilters.questionnaire_status &&
      `Questionnaire: ${questionnaireFilters.find((item) => item.value === appliedFilters.questionnaire_status)?.label}`,
    appliedFilters.payment_method && `Method: ${appliedFilters.payment_method}`,
    appliedFilters.date_from && `From: ${appliedFilters.date_from}`,
    appliedFilters.date_to && `To: ${appliedFilters.date_to}`,
    appliedFilters.min_amount && `Min: ${formatCurrency(appliedFilters.min_amount)}`,
    appliedFilters.max_amount && `Max: ${formatCurrency(appliedFilters.max_amount)}`,
  ].filter(Boolean);

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getCreditNotes = (order) => order?.credit_notes || [];

  const getLatestCreditNote = (order) => {
    const notes = getCreditNotes(order);
    return notes.length ? notes[0] : null;
  };

  const hasCreditNote = (order) => getCreditNotes(order).length > 0;

  const hasPaidCreditNote = (order) => {
    return getCreditNotes(order).some((note) => note.status === "Paid");
  };

  const hasProcessingCreditNote = (order) => {
    return getCreditNotes(order).some((note) => note.status === "Processing");
  };

  const getPaidRefundTotal = (order) => {
    const totalInPaise = getCreditNotes(order)
      .filter((note) => note.status === "Paid")
      .reduce((total, note) => total + toPaise(note.refund_amount), 0);

    return totalInPaise / 100;
  };

  const getRemainingRefundableAmount = (order) => {
    const paidAmountInPaise = toPaise(
      order?.payment?.amount || order?.total_amount
    );
    return (
      Math.max(
        0,
        paidAmountInPaise - toPaise(getPaidRefundTotal(order))
      ) / 100
    );
  };

  const openOrder = (order) => {
    setSelectedOrder(order);
    setViewSidebarOpen(true);
    refreshSelectedOrder(order.order_id);
  };

  const formatTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getCustomer = (order) => {
    const value = order?.customer_snapshot;
    if (!value) return {};
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  };

  const getAddress = (order, key) => {
    const value = order?.[key];
    if (!value) return {};
    if (typeof value !== "string") return value;
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  };

  const formatAddress = (address) => {
    const locality = [address?.city, address?.state].filter(Boolean).join(", ");
    const countryLine = [address?.country, address?.zipcode]
      .filter(Boolean)
      .join(" - ");

    return [address?.address1, address?.address2, locality, countryLine]
      .filter(Boolean);
  };

  const refreshSelectedOrder = async (orderId) => {
    try {
      const res = await api.get(`/admin/orders/${orderId}`, {
        headers: getPermissionHeaders("view"),
      });
      const data = res.data?.data;

      if (data) {
        setSelectedOrder((current) =>
          current?.order_id === orderId ? data : current
        );
      }
    } catch (error) {
      showRequestError(error, "Failed to refresh order.");
    }
  };
    
  const updateOrderStatus = async (order, payload) => {
    try {
      setStatusUpdatingId(order.order_id);
      await api.put(`/admin/orders/${order.order_id}/status`, {
        ...(typeof payload === "string" ? { order_status: payload } : payload),
      }, {
        headers: getPermissionHeaders("edit"),
      });

      toast.success("Order status updated.");
      await fetchOrders(pagination.page, appliedFilters);

      if (selectedOrder?.order_id === order.order_id) {
        await refreshSelectedOrder(order.order_id);
      }
      return true;
    } catch (error) {
      showRequestError(error, "Status update failed.");
      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  };

  useEffect(() => {
    if (!linkedOrderId) return undefined;
    let active = true;

    api.get(`/admin/orders/${linkedOrderId}`, {
      headers: getPermissionHeaders("view"),
    }).then((response) => {
      if (!active || !response.data?.data) return;
      setSelectedOrder(response.data.data);
      setViewSidebarOpen(true);
    }).catch((error) => {
      if (active) showRequestError(error, "Failed to open the linked order.");
    });

    return () => { active = false; };
  }, [linkedOrderId]);

  const openProductionSetup = (order) => {
    const job = order.production_job || {};

    setSelectedOrder(order);
    setProductionForm({
      assigned_to: job.assigned_to || "",
      assignee_contact: job.assignee_contact || "",
      target_completion_at: job.target_completion_at
        ? toDateTimeLocalValue(job.target_completion_at)
        : getDefaultProductionDeadline(),
      priority: job.priority || "normal",
      production_brief: job.production_brief || "",
    });
    setProductionSidebarOpen(true);
  };

  const openPrintingSetup = async (order) => {
    setSelectedOrder(order);
    setPrintingVendorId(String(order.production_job?.vendor_id || ""));
    setPrintingDialog(true);
    try {
      setPrintingVendorsLoading(true);
      const response = await api.get("/admin/orders/vendors", {
        params: { status: "active", page: 1, limit: 100 },
        headers: getPermissionHeaders("view"),
      });
      setPrintingVendors(response.data?.data || []);
    } catch (error) {
      showRequestError(error, "Failed to load active printing vendors.");
    } finally {
      setPrintingVendorsLoading(false);
    }
  };

  const handleStatusChange = (order, newStatus) => {
    if (newStatus === order.order_status) return;

    if (newStatus === "processing") {
      if (order.questionnaire && order.questionnaire.status !== "submitted") {
        toast.error(
          "The customer must submit the wedding questionnaire before production can begin."
        );
        return;
      }
      openProductionSetup(order);
      return;
    }

    if (newStatus === "printing") {
      if (!order.production_job) {
        toast.error("Start production before assigning a printing vendor.");
        return;
      }
      openPrintingSetup(order);
      return;
    }

    if (newStatus === "shipped" && order.shipping_required) {
      setSelectedOrder(order);
      setShippingForm({
        courier_name: order.courier_name || "",
        tracking_number: order.tracking_number || "",
        tracking_url: order.tracking_url || "",
        estimated_delivery_date: order.estimated_delivery_date
          ? String(order.estimated_delivery_date).slice(0, 10)
          : "",
      });
      setShippingDialog(true);
      return;
    }

    confirmDialog({
      message: `Change order ${order.order_number} to "${newStatus}"?`,
      header: "Update Order Status",
      icon: "pi pi-exclamation-triangle",
      accept: () =>
        updateOrderStatus(order, {
          order_status: newStatus,
        }),
    });
  };

  const validateShippingForm = () => {
    if (!shippingForm.courier_name.trim()) {
      toast.error("Courier name is required.");
      return false;
    }

    if (!shippingForm.tracking_number.trim()) {
      toast.error("Tracking number is required.");
      return false;
    }

    if (!shippingForm.tracking_url.trim()) {
      toast.error("Tracking URL is required.");
      return false;
    }

    if (!/^https?:\/\//i.test(shippingForm.tracking_url.trim())) {
      toast.error("Tracking URL must start with http:// or https://");
      return false;
    }

    return true;
  };

  const submitShippingStatus = async () => {
    if (!selectedOrder) return;
    if (!validateShippingForm()) return;

    try {
      setShippingSubmitting(true);

      await updateOrderStatus(selectedOrder, {
        order_status: "shipped",
        courier_name: shippingForm.courier_name.trim(),
        tracking_number: shippingForm.tracking_number.trim(),
        tracking_url: shippingForm.tracking_url.trim(),
        estimated_delivery_date:
          shippingForm.estimated_delivery_date || null,
      });

      setShippingDialog(false);
      setShippingForm(emptyShippingForm);
    } finally {
      setShippingSubmitting(false);
    }
  };

  const cancelOrder = (order) => {
    confirmDialog({
      message: `Cancel order ${order.order_number}?`,
      header: "Cancel Order",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: async () => {
        try {
          await api.put(`/admin/orders/${order.order_id}/cancel`, {}, {
            headers: getPermissionHeaders("edit"),
          });

          toast.success("Order cancelled.");
          await fetchOrders(pagination.page, appliedFilters);
          setViewSidebarOpen(false);
        } catch (error) {
          showRequestError(error, "Cancel failed.");
        }
      },
    });
  };

  const refundOrder = (order) => {
    const remainingAmount = getRemainingRefundableAmount(order);
    setSelectedOrder(order);
    setRefundForm({
      amount: remainingAmount.toFixed(2),
      remark: "",
    });
    setRefundDialog(true);
  };

  const submitPrintingStatus = async () => {
    if (!selectedOrder || !printingVendorId) {
      toast.error("Select a printing vendor.");
      return;
    }
    try {
      setPrintingSubmitting(true);
      const saved = await updateOrderStatus(selectedOrder, {
        order_status: "printing",
        vendor_id: Number(printingVendorId),
      });
      if (saved) {
        setPrintingDialog(false);
        setPrintingVendorId("");
      }
    } finally {
      setPrintingSubmitting(false);
    }
  };

  const submitProductionSetup = async () => {
    if (!selectedOrder) return;
    if (!productionForm.assigned_to.trim()) {
      toast.error("Assign this job to a team member or vendor.");
      return;
    }
    if (!productionForm.target_completion_at) {
      toast.error("Select a production completion deadline.");
      return;
    }

    try {
      setProductionSubmitting(true);
      const saved = await updateOrderStatus(selectedOrder, {
        order_status: "processing",
        ...productionForm,
        target_completion_at: dateTimeLocalToIso(productionForm.target_completion_at),
        assigned_to: productionForm.assigned_to.trim(),
        assignee_contact: productionForm.assignee_contact.trim(),
        production_brief: productionForm.production_brief.trim(),
      });

      if (saved) {
        setProductionSidebarOpen(false);
        setProductionForm(emptyProductionForm);
        toast.success("Production job created. You can manage it from Production Management.");
      }
    } finally {
      setProductionSubmitting(false);
    }
  };

  const refundOtpResendSeconds = Math.max(
    0,
    Math.ceil((refundOtpResendAt - refundOtpNow) / 1000)
  );

  useEffect(() => {
    if (!refundOtpDialog) return undefined;

    const timer = setInterval(() => setRefundOtpNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [refundOtpDialog]);

  const requestRefundOtp = async () => {
    if (!selectedOrder) return;

    try {
      setRefundOtpSending(true);
      const res = await api.post(
        `/admin/orders/${selectedOrder.order_id}/refund/otp`,
        {
          amount: Number(refundForm.amount),
          remark: refundForm.remark.trim(),
        },
        {
          headers: getPermissionHeaders("edit"),
        }
      );
      const data = res.data?.data || {};

      setRefundOtpInfo(data);
      setRefundOtp("");
      setRefundOtpNow(Date.now());
      setRefundOtpResendAt(
        Date.now() + (Number(data.resend_after_seconds) || 60) * 1000
      );
      setRefundOtpDialog(true);
      toast.success(res.data?.message || "Approval code sent.");
    } catch (error) {
      const waitSeconds = Number(error.response?.data?.data?.resend_after_seconds);
      if (waitSeconds > 0) {
        setRefundOtpNow(Date.now());
        setRefundOtpResendAt(Date.now() + waitSeconds * 1000);
      }

      showRequestError(error, "Could not send the refund approval code.");

      // Return to the form when the first send fails, so nothing is lost.
      if (!refundOtpDialog) setRefundDialog(true);
    } finally {
      setRefundOtpSending(false);
    }
  };

  const closeRefundOtpDialog = () => {
    setRefundOtpDialog(false);
    setRefundOtp("");
    setRefundOtpInfo(null);
  };

  const confirmCashfreeRefund = async () => {
    if (!/^\d{6}$/.test(refundOtp)) {
      toast.error("Enter the 6-digit approval code.");
      return;
    }

    try {
      setRefundSubmitting(true);
      const res = await api.post(
        `/admin/orders/${selectedOrder.order_id}/refund`,
        {
          amount: Number(refundForm.amount),
          remark: refundForm.remark.trim(),
          otp: refundOtp,
        },
        {
          headers: getPermissionHeaders("edit"),
        }
      );

      toast.success(res.data?.message || "Refund submitted to Cashfree.");

      closeRefundOtpDialog();
      setRefundDialog(false);
      setRefundForm(emptyRefundForm);
      await fetchOrders(pagination.page, appliedFilters);
      await refreshSelectedOrder(selectedOrder.order_id);
    } catch (error) {
      if (error.response?.data?.code === "REFUND_OTP_INVALID") {
        setRefundOtp("");
      } else {
        // The code is consumed once verified, so a failed refund needs a new one.
        closeRefundOtpDialog();
      }
      showRequestError(error, "Cashfree refund failed.");
    } finally {
      setRefundSubmitting(false);
    }
  };

  const requestFinalRefundConfirmation = () => {
    if (!selectedOrder) return;

    const refundAmount = Number(refundForm.amount);
    const remainingAmount = getRemainingRefundableAmount(selectedOrder);
    const remark = refundForm.remark.trim();

    if (!Number.isFinite(refundAmount) || refundAmount <= 0) {
      toast.error("Enter a valid refund amount greater than zero.");
      return;
    }

    if (Math.abs(refundAmount * 100 - Math.round(refundAmount * 100)) > 0.000001) {
      toast.error("Refund amount can have at most two decimal places.");
      return;
    }

    if (Math.round(refundAmount * 100) > Math.round(remainingAmount * 100)) {
      toast.error(
        `Only ${formatCurrency(remainingAmount)} remains refundable for this order.`
      );
      return;
    }

    if (remark.length < 3) {
      toast.error("Add a refund remark explaining what the refund is against.");
      return;
    }

    setRefundDialog(false);
    confirmDialog({
      header: "Final Refund Confirmation",
      message: `Refund ${formatCurrency(
        refundAmount
      )} for ${selectedOrder.order_number}? Remark: “${remark}”. An approval code will be emailed to the refund approvers. Once you enter it, Cashfree receives the request immediately and the refund cannot be undone.`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Send Approval Code",
      rejectLabel: "Go Back",
      acceptClassName: "p-button-danger",
      accept: requestRefundOtp,
      reject: () => setRefundDialog(true),
    });
  };

  const generateInvoice = async (order) => {
    try {
      const res = await api.post(`/admin/invoice/generate/${order.order_id}`, {}, {
        headers: getPermissionHeaders("edit"),
      });

      toast.success("Invoice generated successfully.");

      setSelectedOrder((prev) => ({
        ...prev,
        invoice: res.data?.data,
      }));

      await fetchOrders(pagination.page, appliedFilters);
    } catch (error) {
      showRequestError(error, "Invoice generation failed.");
    }
  };

  const renderSelectedOptions = (item) => {
    const selectedOptions = normaliseSelectedOptions(item.selected_options)
      .sort(
          (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
        );

    if (!selectedOptions.length) return null;

    return (
      <div className="admin-order-form-data">
        <strong>Selected Options</strong>

        {selectedOptions.map((option, index) => (
          <small key={`${option.field_id || "field"}-${option.value_id || index}`}>
            <b>{option.field_name || option.template_name || "Option"}:</b>{" "}
            {option.label || option.value || "-"}
            {Number(option.price_adjustment || 0) > 0
              ? ` (+₹${Number(option.price_adjustment).toLocaleString()})`
              : ""}
          </small>
        ))}
      </div>
    );
  };

  const renderSubmittedDetails = (item) => {
    const formValues = [...(item.form_values || [])].sort(
      (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
    );

    if (!formValues.length) return null;

    return (
      <div className="admin-order-form-data">
        <strong>Submitted Details</strong>

        {formValues.map((field) => (
          <small key={field.order_item_form_value_id}>
            <b>{field.field_label}:</b>{" "}
            {field.input_type === "file" && field.file_url ? (
              <a href={field.file_url} target="_blank" rel="noreferrer">
                {field.file_name || "View file"}
              </a>
            ) : (
              field.value || "-"
            )}
          </small>
        ))}
      </div>
    );
  };

  const renderShippingBox = () => {
    if (!selectedOrder) return null;

    if (!selectedOrder.shipping_required) {
      return (
        <div className="customer-restrict-box">
          <h3>Shipping Details</h3>
          <p>
            <strong>Shipping:</strong> Not required
          </p>
          <p>
            <strong>Delivery Type:</strong> Digital only
          </p>
        </div>
      );
    }

    return (
      <div className="customer-restrict-box">
        <h3>Shipping Details</h3>

        <p>
          <strong>Status:</strong>{" "}
          <span className={`status-badge ${selectedOrder.shipping_status}`}>
            {selectedOrder.shipping_status || "pending"}
          </span>
        </p>

        <p>
          <strong>Courier:</strong> {selectedOrder.courier_name || "-"}
        </p>

        <p>
          <strong>Tracking Number:</strong>{" "}
          {selectedOrder.tracking_number || "-"}
        </p>

        <p>
          <strong>Tracking URL:</strong>{" "}
          {selectedOrder.tracking_url ? (
            <a
              href={selectedOrder.tracking_url}
              target="_blank"
              rel="noreferrer"
            >
              Track Package
            </a>
          ) : (
            "-"
          )}
        </p>

        <p>
          <strong>Shipped At:</strong> {formatDate(selectedOrder.shipped_at)}
        </p>

        <p>
          <strong>Estimated Delivery:</strong>{" "}
          {formatDate(selectedOrder.estimated_delivery_date)}
        </p>

        <p>
          <strong>Delivered At:</strong> {formatDate(selectedOrder.delivered_at)}
        </p>
      </div>
    );
  };

  const renderInvoiceBox = () => {
    if (!selectedOrder) return null;

    const invoice = selectedOrder.invoice;

    return (
      <div className="customer-restrict-box premium-doc-box">
        <div className="d-flex align-items-center justify-content-between">
          <h3>Invoice</h3>
        </div>

        {invoice ? (
          <>
            <p>
              <strong>Invoice No:</strong>{" "}
              {invoice.invoice_number || invoice.Inv_no || "-"}
            </p>

            <p>
              <strong>Status:</strong>{" "}
              <span className={`status-badge ${invoice.payment_status}`}>
                {invoice.payment_status || "-"}
              </span>
            </p>

            <p>
              <strong>Date:</strong>{" "}
              {formatDate(invoice.invoice_date || invoice.date)}
            </p>

            <p>
              <strong>Total:</strong>{" "}
              {formatCurrency(invoice.final_price || selectedOrder.total_amount)}
            </p>

            {invoice.invoice_pdf_url && (
              <p>
                <a href={invoice.invoice_pdf_url} target="_blank" rel="noreferrer">
                  View Invoice PDF
                </a>
              </p>
            )}
          </>
        ) : selectedOrder.payment_status === "paid" ? (
          <>
            <p>No invoice generated yet.</p>

            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={() => generateInvoice(selectedOrder)}
            >
              Generate Invoice
            </button>
          </>
        ) : (
          <p>Invoice can be generated after successful payment.</p>
        )}
      </div>
    );
  };

  const renderCreditNoteBox = () => {
    if (!selectedOrder || !hasCreditNote(selectedOrder)) return null;

    return (
      <div className="credit-note-panel">
        <div className="credit-note-head">
          <div>
            <span>Credit Note</span>
            <h3>Refund Document</h3>
          </div>
        </div>

        {getCreditNotes(selectedOrder).map((note) => (
          <div className="credit-note-card" key={note.credit_note_id}>
            <div className="credit-note-row">
              <strong>{note.credit_note_number}</strong>
              <span className={`status-badge ${note.status}`}>{note.status}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", margin: "10px 0" }}>
              <p>
                <strong>Refund Amount:</strong> {formatCurrency(note.refund_amount)}
              </p>

              <p>
                <strong>Refund Type:</strong>{" "}
                {Number(note.refund_amount) >=
                Number(selectedOrder.payment?.amount || selectedOrder.total_amount)
                  ? "Full refund"
                  : "Partial refund"}
              </p>

              <p>
                <strong>Remark / Against:</strong> {note.reason || "-"}
              </p>

              <p>
                <strong>Invoice:</strong> {note.invoice_number || "-"}
              </p>

              <p>
                <strong>Visible to Customer:</strong>{" "}
                {note.is_visible ? "Yes" : "No"}
              </p>
            </div>

            {note.status === "Processing" && (
              <div className="manual-refund-alert compact">
                <TbAlertTriangle />
                <span>
                  Cashfree is processing this refund. The status will update
                  automatically after the refund webhook is received.
                </span>
              </div>
            )}

            {note.status === "Paid" && (
              <div className="refund-success-strip">
                Refund completed successfully and credit note is visible to the
                customer.
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <section className="admin-page premium-users-page">
      <ConfirmDialog />
      <Tooltip id="orderActionTip" />

      <Sidebar
        visible={productionSidebarOpen}
        position="right"
        onHide={() => !productionSubmitting && setProductionSidebarOpen(false)}
        className="production-setup-sidebar"
      >
        <div className="production-setup-shell">
          <div className="production-setup-heading">
            <span>Start production</span>
            <h2>{selectedOrder?.order_number}</h2>
            <p>
              Assign ownership and set the promised production timeline before
              this order moves to processing.
            </p>
          </div>

          <div className="production-order-glance">
            <div><strong>{selectedOrder?.items?.length || 0}</strong><span>Products</span></div>
            <div><strong>{formatCurrency(selectedOrder?.total_amount)}</strong><span>Order value</span></div>
            <div><strong>{selectedOrder?.customer_snapshot?.name || "Customer"}</strong><span>Client</span></div>
          </div>

          <div className="production-form-grid">
            <label>
              Assigned to *
              <input
                value={productionForm.assigned_to}
                onChange={(event) => setProductionForm((prev) => ({ ...prev, assigned_to: event.target.value }))}
                placeholder="Team member, designer or vendor"
              />
            </label>
            <label>
              Contact
              <input
                value={productionForm.assignee_contact}
                onChange={(event) => setProductionForm((prev) => ({ ...prev, assignee_contact: event.target.value }))}
                placeholder="Phone or internal contact"
              />
            </label>
            <label>
              Completion deadline *
              <input
                type="datetime-local"
                value={productionForm.target_completion_at}
                onChange={(event) => setProductionForm((prev) => ({ ...prev, target_completion_at: event.target.value }))}
              />
            </label>
            <label>
              Priority
              <select
                value={productionForm.priority}
                onChange={(event) => setProductionForm((prev) => ({ ...prev, priority: event.target.value }))}
              >
                <option value="low">Low</option>
                <option value="normal">Normal</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </label>
            <label className="production-form-wide">
              Production brief
              <textarea
                rows="6"
                value={productionForm.production_brief}
                onChange={(event) => setProductionForm((prev) => ({ ...prev, production_brief: event.target.value }))}
                placeholder="Internal production instructions, material notes, checks or hand-off details..."
              />
            </label>
          </div>

          <div className="production-setup-note">
            Product images, selected options and client requirements will be
            included automatically in the downloadable job card.
          </div>

          <div className="production-setup-actions">
            <button type="button" className="ghost-action bottonborder" onClick={() => setProductionSidebarOpen(false)} disabled={productionSubmitting}>
              Cancel
            </button>
            <button type="button" className="primary-action" onClick={submitProductionSetup} disabled={productionSubmitting}>
              {productionSubmitting ? "Starting..." : "Start Production"}
            </button>
          </div>
        </div>
      </Sidebar>

      <Dialog
        visible={printingDialog}
        onHide={() => !printingSubmitting && setPrintingDialog(false)}
        header={
          <div className="shipping-dialog-title">
            <span><TbBuildingFactory2 /></span>
            <div><small>Production workflow</small><strong>Start printing</strong></div>
          </div>
        }
        className="shipping-order-dialog printing-vendor-dialog"
        modal
        draggable={false}
        footer={
          <div className="shipping-dialog-actions">
            <button type="button" className="ghost-action bottonborder" disabled={printingSubmitting} onClick={() => setPrintingDialog(false)}>Cancel</button>
            <button type="button" className="primary-action" disabled={printingSubmitting || printingVendorsLoading || !printingVendorId} onClick={submitPrintingStatus}>{printingSubmitting ? "Updating..." : "Assign & start printing"}</button>
          </div>
        }
      >
        <div className="printing-vendor-content">
          <p>Select the vendor responsible for printing order <strong>{selectedOrder?.order_number}</strong>. This assignment is visible only to admins.</p>
          <label>
            <span>Printing vendor *</span>
            <select value={printingVendorId} disabled={printingVendorsLoading} onChange={(event) => setPrintingVendorId(event.target.value)}>
              <option value="">{printingVendorsLoading ? "Loading vendors..." : "Select an active vendor"}</option>
              {printingVendors.map((vendor) => <option value={vendor.vendor_id} key={vendor.vendor_id}>{vendor.name} ({vendor.vendor_code})</option>)}
            </select>
          </label>
          {!printingVendorsLoading && printingVendors.length === 0 && <div className="manual-refund-alert"><TbAlertTriangle /><span>No active vendors are available. Add or activate a vendor from the Vendors page first.</span></div>}
        </div>
      </Dialog>

      <Dialog
        visible={shippingDialog}
        onHide={() => {
          setShippingDialog(false);
          setShippingForm(emptyShippingForm);
        }}
        header={
          <div className="shipping-dialog-title">
            <span><TbPackage /></span>
            <div>
              <small>Order fulfilment</small>
              <strong>Ship order</strong>
            </div>
          </div>
        }
        className="shipping-order-dialog"
        modal
        draggable={false}
        footer={
          <div className="shipping-dialog-actions">
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={() => {
                setShippingDialog(false);
                setShippingForm(emptyShippingForm);
              }}
              disabled={shippingSubmitting}
            >
              Cancel
            </button>

            <button
              type="button"
              className="primary-action"
              onClick={submitShippingStatus}
              disabled={shippingSubmitting}
            >
              {shippingSubmitting ? "Saving..." : "Ship Order"}
            </button>
          </div>
        }
      >
        <div className="shipping-dialog-body">
          <div className="shipping-order-summary">
            <span className="shipping-order-summary-icon">
              <TbPackage />
            </span>

            <div>
              <small>Order</small>
              <strong>{selectedOrder?.order_number}</strong>
              <span>{selectedOrder?.customer_snapshot?.name || "Customer"}</span>
            </div>

            <div className="shipping-order-value">
              <small>Order value</small>
              <strong>{formatCurrency(selectedOrder?.total_amount)}</strong>
            </div>
          </div>

          <div className="shipping-dialog-note">
            Add the delivery details below. The customer can use this information
            to follow the shipment after the order is marked as shipped.
          </div>

          <div className="shipping-form-grid">
            <label className="shipping-field">
              <span>Courier name <em>*</em></span>
              <input
                value={shippingForm.courier_name}
                onChange={(event) =>
                  setShippingForm((prev) => ({
                    ...prev,
                    courier_name: event.target.value,
                  }))
                }
                placeholder="e.g. Blue Dart, Delhivery or DTDC"
                autoComplete="organization"
              />
            </label>

            <label className="shipping-field">
              <span>Tracking number <em>*</em></span>
              <input
                value={shippingForm.tracking_number}
                onChange={(event) =>
                  setShippingForm((prev) => ({
                    ...prev,
                    tracking_number: event.target.value,
                  }))
                }
                placeholder="Enter tracking or AWB number"
                autoComplete="off"
              />
            </label>

            <label className="shipping-field shipping-field-wide">
              <span>Tracking URL <em>*</em></span>
              <input
                type="url"
                value={shippingForm.tracking_url}
                onChange={(event) =>
                  setShippingForm((prev) => ({
                    ...prev,
                    tracking_url: event.target.value,
                  }))
                }
                placeholder="https://courier.example/track/..."
                autoComplete="url"
              />
            </label>

            <label className="shipping-field shipping-field-wide">
              <span>Estimated delivery date</span>
              <input
                type="date"
                value={shippingForm.estimated_delivery_date}
                onChange={(event) =>
                  setShippingForm((prev) => ({
                    ...prev,
                    estimated_delivery_date: event.target.value,
                  }))
                }
              />
            </label>
          </div>

          <p className="shipping-dialog-hint">
            Required fields are marked with an asterisk. Please verify the tracking
            link before notifying the customer.
          </p>
        </div>
      </Dialog>

      <Dialog
        visible={refundDialog}
        onHide={() => setRefundDialog(false)}
        header="Refund Through Cashfree"
        className="premium-refund-dialog"
        modal
        draggable={false}
        footer={
          <div className="refund-dialog-actions">
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={() => setRefundDialog(false)}
              disabled={refundSubmitting}
            >
              Cancel
            </button>

            <button
              type="button"
              className="danger-action bottonborder"
              onClick={requestFinalRefundConfirmation}
              disabled={refundSubmitting}
            >
              Continue
            </button>
          </div>
        }
      >
        {selectedOrder && (
          <div className="manual-refund-dialog-body">
            <div className="manual-refund-icon">
              <TbAlertTriangle />
            </div>

            <h3>Create a partial or full refund</h3>

            <p>
              Enter the amount to refund for order{" "}
              <strong>{selectedOrder.order_number}</strong>. The remaining
              refundable balance is{" "}
              <strong>
                {formatCurrency(getRemainingRefundableAmount(selectedOrder))}
              </strong>.
            </p>

            <div className="refund-form-grid">
              <label className="refund-field">
                <span>Refund amount <em>*</em></span>
                <input
                  type="number"
                  min="0.01"
                  max={getRemainingRefundableAmount(selectedOrder)}
                  step="0.01"
                  value={refundForm.amount}
                  onChange={(event) =>
                    setRefundForm((current) => ({
                      ...current,
                      amount: event.target.value,
                    }))
                  }
                  disabled={refundSubmitting}
                />
                <small>
                  Use the full remaining balance for a full refund, or a smaller
                  amount for a partial refund.
                </small>
              </label>

              <label className="refund-field">
                <span>Refund remark / against <em>*</em></span>
                <textarea
                  rows="3"
                  maxLength="500"
                  value={refundForm.remark}
                  onChange={(event) =>
                    setRefundForm((current) => ({
                      ...current,
                      remark: event.target.value,
                    }))
                  }
                  placeholder="Example: Cancellation of 100 invitation cards from this order"
                  disabled={refundSubmitting}
                />
                <small>
                  This remark will appear on the credit note for both partial and
                  full refunds.
                </small>
              </label>
            </div>

            <div className="manual-refund-alert">
              <TbBellRinging />
              <span>
                You cannot undo this action after it is submitted. After a final
                confirmation, an approval code is emailed to the refund approvers
                and must be entered before Cashfree is called.
              </span>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        visible={refundOtpDialog}
        onHide={() => {
          if (!refundSubmitting) closeRefundOtpDialog();
        }}
        header="Refund Approval Code"
        className="premium-refund-dialog"
        modal
        draggable={false}
        closable={!refundSubmitting}
        footer={
          <div className="refund-dialog-actions">
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={() => {
                closeRefundOtpDialog();
                setRefundDialog(true);
              }}
              disabled={refundSubmitting}
            >
              Back
            </button>

            <button
              type="button"
              className="danger-action bottonborder"
              onClick={confirmCashfreeRefund}
              disabled={refundSubmitting || refundOtp.length !== 6}
            >
              {refundSubmitting ? "Refunding..." : "Verify & Refund"}
            </button>
          </div>
        }
      >
        {selectedOrder && (
          <div className="manual-refund-dialog-body">
            <div className="manual-refund-icon">
              <TbReceiptRefund />
            </div>

            <h3>Enter the approval code</h3>

            <p>
              A 6-digit code for refunding{" "}
              <strong>{formatCurrency(Number(refundForm.amount))}</strong> on{" "}
              <strong>{selectedOrder.order_number}</strong> was sent to{" "}
              <strong>
                {(refundOtpInfo?.sent_to || []).join(", ") || "the refund approvers"}
              </strong>
              . It expires in {refundOtpInfo?.expires_in_minutes || 10} minutes.
            </p>

            <div className="refund-form-grid">
              <label className="refund-field">
                <span>Approval code <em>*</em></span>
                <input
                  className="refund-otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={refundOtp}
                  onChange={(event) =>
                    setRefundOtp(event.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && refundOtp.length === 6) {
                      confirmCashfreeRefund();
                    }
                  }}
                  placeholder="000000"
                  disabled={refundSubmitting}
                  autoFocus
                />
                <small>
                  Ask an approver for the code. It only works for this order,
                  amount and remark, and can be used once.
                </small>
              </label>
            </div>

            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={requestRefundOtp}
              disabled={
                refundSubmitting || refundOtpSending || refundOtpResendSeconds > 0
              }
            >
              {refundOtpSending
                ? "Sending..."
                : refundOtpResendSeconds > 0
                  ? `Resend code in ${refundOtpResendSeconds}s`
                  : "Resend code"}
            </button>
          </div>
        )}
      </Dialog>

      <div className="page-header users-hero-header">
        <div>
          <p>View placed orders</p>
          <h1>Orders</h1>
        </div>
        <Link to="/production" className="primary-action production-page-link">
          <TbRefresh />
          Production Management
        </Link>
      </div>

      <div className="page-card premium-user-card orders-table-card">
        <div className="orders-table-toolbar">
          <div>
            <h2>Orders</h2>
            <p>
              {pagination.total} order{pagination.total === 1 ? "" : "s"} found
            </p>
          </div>
          <div className="orders-table-filters">
            <label className="orders-search-field">
              <TbSearch />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search customer, product, order, invoice..."
                aria-label="Search orders by customer, product, order, invoice, payment, or tracking details"
              />
            </label>
            <button
              type="button"
              className={`orders-filter-toggle ${activeFilterLabels.length ? "active" : ""}`}
              onClick={() => setFiltersOpen((current) => !current)}
              aria-expanded={filtersOpen}
            >
              <TbAdjustmentsHorizontal />
              Filters
              {activeFilterLabels.length > 0 && <span>{activeFilterLabels.length}</span>}
            </button>
          </div>
        </div>

        <p className="orders-search-hint">
          Searches customer name, email and phone, product name and SKU, order,
          invoice, payment, refund, tracking, address and production details.
        </p>

        {filtersOpen && (
          <form className="orders-advanced-filters" onSubmit={applyFilters}>
            <div className="orders-filter-grid">
              <label>
                <span>Order status</span>
                <select
                  value={filterDraft.order_status}
                  onChange={(event) => updateFilterDraft("order_status", event.target.value)}
                >
                  {orderFilters.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Payment status</span>
                <select
                  value={filterDraft.payment_status}
                  onChange={(event) => updateFilterDraft("payment_status", event.target.value)}
                >
                  {paymentFilters.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Fulfilment</span>
                <select
                  value={filterDraft.shipping_status}
                  onChange={(event) => updateFilterDraft("shipping_status", event.target.value)}
                >
                  {shippingFilters.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Questionnaire</span>
                <select
                  value={filterDraft.questionnaire_status}
                  onChange={(event) => updateFilterDraft("questionnaire_status", event.target.value)}
                >
                  {questionnaireFilters.map((filter) => (
                    <option key={filter.value} value={filter.value}>{filter.label}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Payment method</span>
                <input
                  value={filterDraft.payment_method}
                  onChange={(event) => updateFilterDraft("payment_method", event.target.value)}
                  placeholder="Card, UPI, net banking..."
                />
              </label>
              <label>
                <span>Placed from</span>
                <input
                  type="date"
                  value={filterDraft.date_from}
                  onChange={(event) => updateFilterDraft("date_from", event.target.value)}
                />
              </label>
              <label>
                <span>Placed to</span>
                <input
                  type="date"
                  value={filterDraft.date_to}
                  onChange={(event) => updateFilterDraft("date_to", event.target.value)}
                />
              </label>
              <label>
                <span>Minimum amount</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={filterDraft.min_amount}
                  onChange={(event) => updateFilterDraft("min_amount", event.target.value)}
                  placeholder="0.00"
                />
              </label>
              <label>
                <span>Maximum amount</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={filterDraft.max_amount}
                  onChange={(event) => updateFilterDraft("max_amount", event.target.value)}
                  placeholder="No maximum"
                />
              </label>
            </div>
            <div className="orders-filter-actions">
              <button type="button" className="orders-clear-filters" onClick={clearFilters}>
                <TbX /> Clear all
              </button>
              <button type="submit" className="orders-apply-filters">
                <TbAdjustmentsHorizontal /> Apply filters
              </button>
            </div>
          </form>
        )}

        {activeFilterLabels.length > 0 && (
          <div className="orders-active-filters" aria-label="Active order filters">
            <strong>Active</strong>
            {activeFilterLabels.map((label) => <span key={label}>{label}</span>)}
            <button type="button" onClick={clearFilters}>Clear all</button>
          </div>
        )}

        <div className={`orders-table-scroll ${loading ? "is-loading" : ""}`}>
          <table className="orders-data-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Amount</th>
                <th>Placed</th>
                <th>Order status</th>
                <th>Payment</th>
                <th>Fulfilment</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {!loading && orders.length === 0 && (
                <tr>
                  <td colSpan="7" className="orders-table-empty">
                    <TbPackage />
                    <strong>No orders found</strong>
                    <span>Try a different status or search term.</span>
                  </td>
                </tr>
              )}

              {loading && orders.length === 0 && (
                <tr>
                  <td colSpan="7" className="orders-table-empty">
                    <span>Loading orders…</span>
                  </td>
                </tr>
              )}

              {orders.map((order) => {
                const customer = getCustomer(order);
                const creditNote = getLatestCreditNote(order);
                const refundPending = order.payment_status === "refund_pending";
                const refunded = order.payment_status === "refunded";
                const rowBusy = statusUpdatingId === order.order_id;

                return (
                  <tr
                    className={`${refundPending ? "refund-pending-row" : ""} ${
                      refunded ? "refund-completed-row" : ""
                    }`}
                    key={order.order_id}
                  >
                    <td className="orders-order-cell">
                      <div className="orders-order-identity">
                        <div className="orders-table-avatar">
                          {refundPending ? <TbAlertTriangle /> : <TbPackage />}
                        </div>
                        <div>
                          <strong>{order.order_number}</strong>
                          <span>{customer.name || "Customer"}</span>
                          <small>{order.items?.length || 0} product(s)</small>
                        </div>
                      </div>
                    </td>
                    <td className="orders-amount-cell">
                      <strong>{formatCurrency(order.total_amount)}</strong>
                      {order.invoice?.invoice_number && (
                        <small>{order.invoice.invoice_number}</small>
                      )}
                    </td>
                    <td className="orders-date-cell">
                      <strong>{formatDate(order.createdAt)}</strong>
                      <span>{formatTime(order.createdAt)}</span>
                    </td>
                    <td>
                      <select
                        className={`orders-inline-status status-${order.order_status}`}
                        value={order.order_status}
                        disabled={rowBusy}
                        onChange={(event) =>
                          handleStatusChange(order, event.target.value)
                        }
                        aria-label={`Change status for ${order.order_number}`}
                      >
                        {orderStatusOptions.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={
                              option.value === "processing" &&
                              order.questionnaire &&
                              order.questionnaire.status !== "submitted"
                            }
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="orders-payment-cell">
                      <span className={`status-badge ${order.payment_status}`}>
                        {String(order.payment_status || "pending").replace(/_/g, " ")}
                      </span>
                      {creditNote && (
                        <small>
                          {creditNote.credit_note_number} · {creditNote.status}
                        </small>
                      )}
                      {getPaidRefundTotal(order) > 0 &&
                        getRemainingRefundableAmount(order) > 0 && (
                          <small>
                            Partially refunded:{" "}
                            {formatCurrency(getPaidRefundTotal(order))}
                          </small>
                        )}
                    </td>
                    <td className="orders-fulfilment-cell">
                      <span className={`status-badge ${order.shipping_status || "pending"}`}>
                        {order.shipping_required
                          ? `Shipping: ${order.shipping_status || "pending"}`
                          : "No shipping"}
                      </span>
                      {order.questionnaire && (
                        <Link
                          to={`/questionnaires?search=${encodeURIComponent(
                            order.order_number || ""
                          )}`}
                          className={`orders-questionnaire-link questionnaire-${order.questionnaire.status}`}
                        >
                          Questionnaire: {order.questionnaire.status}
                        </Link>
                      )}
                    </td>
                    <td className="orders-row-action">
                      <button
                        type="button"
                        data-tooltip-id="orderActionTip"
                        data-tooltip-content="View order details"
                        onClick={() => openOrder(order)}
                        aria-label={`View ${order.order_number}`}
                      >
                        <TbEyeSearch />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="orders-pagination">
          <button
            type="button"
            className="orders-pagination-direction"
            disabled={pagination.page <= 1 || loading}
            onClick={() => fetchOrders(pagination.page - 1, appliedFilters)}
          >
            <GrPrevious /> Previous
          </button>

          <div className="orders-pagination-pages">
            {getPaginationItems(pagination.page, pagination.totalPages).map(
              (item) =>
                typeof item === "number" ? (
                  <button
                    key={item}
                    type="button"
                    className={pagination.page === item ? "active" : ""}
                    onClick={() => fetchOrders(item, appliedFilters)}
                    disabled={loading}
                    aria-label={`Go to page ${item}`}
                    aria-current={pagination.page === item ? "page" : undefined}
                  >
                    {item}
                  </button>
                ) : (
                  <span key={item}>…</span>
                )
            )}
          </div>

          <button
            type="button"
            className="orders-pagination-direction"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => fetchOrders(pagination.page + 1, appliedFilters)}
          >
            Next <GrNext />
          </button>
        </div>
      </div>

      <Sidebar
        visible={viewSidebarOpen}
        position="right"
        onHide={() => setViewSidebarOpen(false)}
        className="premium-user-sidebar order-detail-sidebar"
      >
        {selectedOrder && (
          <div className="user-view-shell order-detail-shell">
            <header className="order-detail-hero">
              <div
                className={`user-view-title ${
                selectedOrder.payment_status === "refund_pending"
                  ? "refund-title-warning"
                  : ""
                }`}
              >
                <span>Order details</span>
                <h2>{selectedOrder.order_number}</h2>
                <p>
                  {getCustomer(selectedOrder).name || "Customer"}
                  <b aria-hidden="true">·</b>
                  {formatDate(selectedOrder.createdAt)} at {formatTime(selectedOrder.createdAt)}
                </p>
              </div>

              <div className="order-detail-total">
                <span>Order total</span>
                <strong>{formatCurrency(selectedOrder.total_amount)}</strong>
                <small>{(selectedOrder.items || []).length} product(s)</small>
              </div>
            </header>

            {selectedOrder.payment_status === "refund_pending" && (
              <div className="manual-refund-alert sidebar-alert">
                <TbAlertTriangle />
                <span style={{ fontSize: "13px" }}>
                  Cashfree is processing this refund. This page will update after
                  the refund webhook confirms its final status.
                </span>
              </div>
            )}

            {selectedOrder.payment_status === "refunded" &&
              hasPaidCreditNote(selectedOrder) && (
                <div className="refund-success-strip sidebar-alert">
                  Refund completed. Credit note is paid and customer-visible.
                </div>
              )}

            {selectedOrder.payment_status === "paid" &&
              getPaidRefundTotal(selectedOrder) > 0 &&
              getRemainingRefundableAmount(selectedOrder) > 0 && (
                <div className="refund-success-strip sidebar-alert">
                  Partial refund completed:{" "}
                  {formatCurrency(getPaidRefundTotal(selectedOrder))} refunded,
                  with {formatCurrency(
                    getRemainingRefundableAmount(selectedOrder)
                  )} still refundable.
                </div>
              )}

            <div className="order-detail-status-grid">
              <div>
                <strong>Order status</strong>
                <span className={`status-badge ${selectedOrder.order_status}`}>
                  {String(selectedOrder.order_status || "pending").replace(/_/g, " ")}
                </span>
              </div>

              <div>
                <strong>Payment status</strong>
                <span className={`status-badge ${selectedOrder.payment_status}`}>
                  {String(selectedOrder.payment_status || "pending").replace(/_/g, " ")}
                </span>
              </div>

              <div>
                <strong>Fulfilment</strong>
                <span className={`status-badge ${selectedOrder.shipping_status}`}>
                  {selectedOrder.shipping_required
                    ? selectedOrder.shipping_status || "pending"
                    : "not required"}
                </span>
              </div>

              <div>
                <strong>Payment method</strong>
                <span>{selectedOrder.payment_method || "Not captured"}</span>
              </div>
            </div>

            <div className="order-detail-layout">
              <main className="order-detail-main">
                <section className="order-detail-card order-products-panel">
                  <div className="order-section-heading">
                    <div>
                      <span>Line items</span>
                      <h3>Products ordered</h3>
                    </div>
                    <strong>{(selectedOrder.items || []).length}</strong>
                  </div>

                  <div className="order-product-list">
                    {(selectedOrder.items || []).map((item, itemIndex) => {
                      const snapshot = item.product_snapshot || {};
                      const displayQuantity = getOptionTemplateQuantity(item);
                      const itemTaxDetails = getOrderItemTaxDetails(
                        selectedOrder,
                        item,
                        itemIndex
                      );
                      const itemTax = itemTaxDetails.tax || {};

                      return (
                        <article className="order-product-card" key={item.order_item_id}>
                          <img
                            src={snapshot.image || "/img/noimage.jpg"}
                            alt={snapshot.name || "Product"}
                          />

                          <div className="order-product-content">
                            <div className="order-product-heading">
                              <div>
                                <strong>{snapshot.name || "Product"}</strong>
                                {snapshot.sku && <small>SKU: {snapshot.sku}</small>}
                              </div>
                              <strong>{formatCurrency(item.total_price)}</strong>
                            </div>

                            <div className="order-product-facts">
                              <span>Qty: <b>{displayQuantity}</b></span>
                              <span>
                                {item.delivery_type === "physical"
                                  ? "Printable"
                                  : "Digital"}
                              </span>
                              {item.unit_price != null && (
                                <span>Rate: <b>{formatCurrency(item.unit_price)}</b></span>
                              )}
                            </div>

                            <div className="order-product-tax">
                              <div className="order-product-tax-heading">
                                <strong>Product tax</strong>
                                <span>
                                  {itemTaxDetails.hsnSacCode
                                    ? `HSN/SAC ${itemTaxDetails.hsnSacCode}`
                                    : "HSN/SAC not captured"}
                                </span>
                              </div>

                              {itemTaxDetails.hasTaxData ? (
                                <dl>
                                  <div>
                                    <dt>Tax rate</dt>
                                    <dd>
                                      {itemTaxDetails.taxType === "INTERNATIONAL"
                                        ? "Export GST 0%"
                                        : itemTaxDetails.taxType === "CGST_SGST"
                                          ? `${Number(itemTax.gst_percentage || 0).toFixed(2)}% GST`
                                          : `${Number(itemTax.gst_percentage || 0).toFixed(2)}% IGST`}
                                    </dd>
                                  </div>
                                  <div>
                                    <dt>Taxable value</dt>
                                    <dd>{formatCurrency(itemTax.taxable_amount)}</dd>
                                  </div>
                                  <div>
                                    <dt>Included tax</dt>
                                    <dd>{formatCurrency(itemTax.tax_amount)}</dd>
                                  </div>
                                  {itemTaxDetails.taxType === "CGST_SGST" && (
                                    <>
                                      <div>
                                        <dt>CGST ({Number(itemTax.cgst_percentage || 0).toFixed(2)}%)</dt>
                                        <dd>{formatCurrency(itemTax.cgst_amount)}</dd>
                                      </div>
                                      <div>
                                        <dt>SGST ({Number(itemTax.sgst_percentage || 0).toFixed(2)}%)</dt>
                                        <dd>{formatCurrency(itemTax.sgst_amount)}</dd>
                                      </div>
                                    </>
                                  )}
                                  {itemTaxDetails.taxType === "IGST" && (
                                    <div>
                                      <dt>IGST ({Number(itemTax.igst_percentage || 0).toFixed(2)}%)</dt>
                                      <dd>{formatCurrency(itemTax.igst_amount)}</dd>
                                    </div>
                                  )}
                                </dl>
                              ) : (
                                <p>Item-level tax was not captured for this order.</p>
                              )}
                            </div>

                            {renderSelectedOptions(item)}
                            {renderSubmittedDetails(item)}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>

                <section className="order-detail-card order-payment-panel">
                  <div className="order-section-heading">
                    <div>
                      <span>Transaction</span>
                      <h3>Payment details</h3>
                    </div>
                  </div>
                  <PaymentGatewayDetails
                    payment={selectedOrder.payment}
                    orderPaymentMethod={selectedOrder.payment_method}
                  />
                </section>
              </main>

              <aside className="order-detail-aside">
                <section className="order-detail-card order-customer-card">
                  <div className="order-section-heading">
                    <div>
                      <span>Customer</span>
                      <h3>{getCustomer(selectedOrder).name || "Customer details"}</h3>
                    </div>
                  </div>
                  {getCustomer(selectedOrder).email && (
                    <p>{getCustomer(selectedOrder).email}</p>
                  )}
                  {getCustomer(selectedOrder).phone && (
                    <p>{getCustomer(selectedOrder).phone}</p>
                  )}
                  {formatAddress(getAddress(selectedOrder, "billing_address")).length > 0 && (
                    <div className="order-address-block">
                      <strong>Billing address</strong>
                      {formatAddress(getAddress(selectedOrder, "billing_address")).map(
                        (line, index) => <span key={`${line}-${index}`}>{line}</span>
                      )}
                    </div>
                  )}
                </section>

                <section className="order-detail-card order-price-card">
                  <div className="order-section-heading">
                    <div>
                      <span>Amounts</span>
                      <h3>Price summary</h3>
                    </div>
                  </div>
                  <dl>
                    <div><dt>Subtotal</dt><dd>{formatCurrency(selectedOrder.subtotal)}</dd></div>
                    <div><dt>Platform fee</dt><dd>{formatCurrency(selectedOrder.platform_fee)}</dd></div>
                    <div><dt>Shipping fee</dt><dd>{formatCurrency(selectedOrder.shipping_fee)}</dd></div>
                    <div><dt>Discount</dt><dd>- {formatCurrency(selectedOrder.discount)}</dd></div>
                    <div className="order-price-total"><dt>Total</dt><dd>{formatCurrency(selectedOrder.total_amount)}</dd></div>
                  </dl>
                </section>

                {selectedOrder.questionnaire && (
                  <section className="order-detail-card order-questionnaire-card">
                    <div className="order-section-heading">
                      <div>
                        <span>Customer input</span>
                        <h3>Wedding questionnaire</h3>
                      </div>
                      <span className={`status-badge ${selectedOrder.questionnaire.status}`}>
                        {selectedOrder.questionnaire.status}
                      </span>
                    </div>
                    <Link to="/questionnaires" className="ghost-action bottonborder">
                      View questionnaire
                    </Link>
                  </section>
                )}

                <div className="order-supporting-cards">
                  {renderInvoiceBox()}
                  {renderCreditNoteBox()}
                  {renderShippingBox()}
                </div>
              </aside>
            </div>

            <div className="user-view-actions order-detail-actions">
              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={() => setViewSidebarOpen(false)}
              >
                Close
              </button>

              <button
                type="button"
                className="danger-action bottonborder"
                disabled={selectedOrder.order_status === "cancelled"}
                onClick={() => cancelOrder(selectedOrder)}
              >
                Cancel Order
              </button>

              {selectedOrder.payment_status === "paid" &&
                getRemainingRefundableAmount(selectedOrder) > 0 &&
                !hasProcessingCreditNote(selectedOrder) && (
                <button
                  type="button"
                  className="danger-action bottonborder"
                  onClick={() => refundOrder(selectedOrder)}
                >
                  <TbCurrencyRupee />
                  Refund up to{" "}
                  {formatCurrency(getRemainingRefundableAmount(selectedOrder))}
                </button>
              )}

              {hasProcessingCreditNote(selectedOrder) && (
                <button
                  type="button"
                  className="ghost-action bottonborder disabled-action"
                  disabled
                >
                  <TbReceiptRefund />
                  Refund Processing
                </button>
              )}

              {getRemainingRefundableAmount(selectedOrder) <= 0 &&
                hasPaidCreditNote(selectedOrder) && (
                  <button
                    type="button"
                    className="ghost-action bottonborder disabled-action"
                    disabled
                  >
                    <TbReceiptRefund />
                    Fully Refunded
                  </button>
                )}
            </div>
          </div>
        )}
      </Sidebar>
    </section>
  );
};

export default Orders;
